import React, { useEffect, useMemo, useState } from "react";
import SiteTablesV2 from "./SiteTablesV2";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { SearchesCIcon } from "../../assets/Icons";
import PortalV2Select from "../common/PortalV2Select";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

const PAGE_SIZE = 10;

const SiteListV2 = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [machineList, setMachineList] = useState([]);
  const [search, setSearch] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchAllMachines = async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/site/main/list/${groupId}`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      });
      setMachineList(data.data || []);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMachines();
  }, []);

  const allTags = useMemo(() => {
    const map = new Map();
    machineList.forEach((site) => {
      site.tags?.forEach((tag) => {
        if (tag?.id) map.set(tag.id, tag.name);
      });
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [machineList]);

  const stats = useMemo(() => {
    const total = machineList.length;
    const active = machineList.filter((s) => s.isActive).length;
    const hidden = total - active;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = machineList.filter((s) => new Date(s.updatedAt).getTime() >= weekAgo).length;
    return { total, active, hidden, recent };
  }, [machineList]);

  const filteredList = useMemo(() => {
    return machineList.filter((item) => {
      const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
      const matchesVisibility =
        visibilityFilter === "all" ||
        (visibilityFilter === "active" && item.isActive) ||
        (visibilityFilter === "hidden" && !item.isActive);
      const matchesTag =
        tagFilter === "all" || item.tags?.some((tag) => String(tag.id) === String(tagFilter));
      return matchesSearch && matchesVisibility && matchesTag;
    });
  }, [machineList, search, visibilityFilter, tagFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, visibilityFilter, tagFilter]);

  const paginatedList = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, page]);

  const resetFilters = () => {
    setSearch("");
    setVisibilityFilter("all");
    setTagFilter("all");
    setPage(1);
  };

  const visibilityOptions = useMemo(
    () => [
      { label: "All", value: "all" },
      { label: "Active", value: "active" },
      { label: "Hidden", value: "hidden" },
    ],
    [],
  );

  const tagOptions = useMemo(
    () => [
      { label: "All Tags", value: "all" },
      ...allTags.map((tag) => ({ label: tag.name, value: String(tag.id) })),
    ],
    [allTags],
  );

  const canAdd = userData?.user?.permissions?.includes("MACHINE.ADD");

  if (loading) {
    return (
      <div className="sites-v2">
        <Skeleton height={36} width={200} />
        <Skeleton height={20} width={320} className="mt-2" />
        <div className="sites-v2__stats mt-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={110} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sites-v2">
      <div className="sites-v2__head">
        <h1 className="sites-v2__title">Sites</h1>
        <p className="sites-v2__subtitle">Manage and monitor all configured sites</p>
      </div>

      <div className="sites-v2__stats">
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--red">
            <ApartmentOutlinedIcon />
          </div>
          <div>
            <strong>{stats.total}</strong>
            <span>Total Sites</span>
            <small>All configured sites</small>
          </div>
        </article>
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--green">
            <CheckCircleOutlineIcon />
          </div>
          <div>
            <strong>{stats.active}</strong>
            <span>Active Sites</span>
            <small>Visible to users</small>
          </div>
        </article>
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--purple">
            <VisibilityOffOutlinedIcon />
          </div>
          <div>
            <strong>{stats.hidden}</strong>
            <span>Hidden Sites</span>
            <small>Not visible to users</small>
          </div>
        </article>
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--amber">
            <ScheduleOutlinedIcon />
          </div>
          <div>
            <strong>{stats.recent}</strong>
            <span>Recently Updated</span>
            <small>In the last 7 days</small>
          </div>
        </article>
      </div>

      {machineList.length === 0 ? (
        <div className="sites-v2__empty">
          <p>No Site Found!</p>
          {canAdd && (
            <button type="button" className="sites-v2__add-btn" onClick={() => navigate(`/site-list/create`)}>
              + Add Site
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="sites-v2__toolbar">
            <label className="sites-v2__search">
              <SearchesCIcon />
              <input
                type="text"
                placeholder="Search sites..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <PortalV2Select
              label="Visibility"
              value={visibilityFilter}
              options={visibilityOptions}
              onChange={setVisibilityFilter}
            />
            <PortalV2Select
              label="Tags"
              value={tagFilter}
              options={tagOptions}
              onChange={setTagFilter}
            />
            <button type="button" className="sites-v2__reset-btn" onClick={resetFilters}>
              ↻ Reset
            </button>
            {canAdd && (
              <button type="button" className="sites-v2__add-btn" onClick={() => navigate(`/site-list/create`)}>
                + Add Site
              </button>
            )}
          </div>

          <SiteTablesV2
            data={paginatedList}
            fetchAllMachines={fetchAllMachines}
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={filteredList.length}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};

export default SiteListV2;
